import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Trash2, Loader2, Search, Phone, Mail, Calendar } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { fetchBookings, fetchStats, updateBookingStatus, deleteBooking, logout, me } from "@/lib/api";
import { Logo } from "@/components/Logo";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const STATUS_STYLES = {
  pending:   "bg-amber-100 text-amber-800 border-amber-200",
  confirmed: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelled: "bg-neutral-200 text-neutral-700 border-neutral-300",
};

const AdminDashboard = () => {
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bs, st] = await Promise.all([fetchBookings(), fetchStats()]);
      setBookings(bs);
      setStats(st);
    } catch (err) {
      if (err?.response?.status === 401) {
        nav("/admin/login");
        return;
      }
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    me().catch(() => nav("/admin/login"));
    load();
  }, [load, nav]);

  const onStatus = async (id, s) => {
    try {
      await updateBookingStatus(id, s);
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status: s } : b)));
      load();
    } catch { toast.error("Update failed"); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await deleteBooking(id);
      setBookings((prev) => prev.filter((b) => b.id !== id));
      load();
    } catch { toast.error("Delete failed"); }
  };
  const onLogout = async () => {
    try { await logout(); } catch { /* ignore */ }
    localStorage.removeItem("mv_token");
    nav("/admin/login");
  };

  const filtered = bookings.filter((b) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [b.name, b.phone, b.email, b.vehicle_make, b.vehicle_model, b.service_needed, b.status]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
  });

  return (
    <div className="min-h-screen bg-brand-surface">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-30">
        <div className="container-mv h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo compact />
            <span className="hidden sm:inline text-sm text-neutral-500">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="inline-flex items-center gap-2 rounded-full border border-neutral-200 px-4 py-2 text-sm hover:border-brand-red hover:text-brand-red transition-colors" data-testid="admin-refresh">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button onClick={onLogout} className="inline-flex items-center gap-2 rounded-full bg-brand-black text-white px-4 py-2 text-sm hover:bg-neutral-800 transition-colors" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="container-mv py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { k: "Total", v: stats.total,     tone: "text-brand-black" },
            { k: "Pending", v: stats.pending, tone: "text-amber-600" },
            { k: "Confirmed", v: stats.confirmed, tone: "text-blue-600" },
            { k: "Completed", v: stats.completed, tone: "text-emerald-600" },
          ].map((s) => (
            <div key={s.k} className="bg-white rounded-xl border border-neutral-200 p-5 shadow-premium">
              <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500">{s.k}</div>
              <div className={`mt-1 font-display font-extrabold text-3xl ${s.tone}`}>{s.v}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <h2 className="font-display font-bold text-2xl text-brand-black">Booking Requests</h2>
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search bookings…" className="pl-9" data-testid="admin-search" />
          </div>
        </div>

        <div className="mt-4 bg-white rounded-2xl border border-neutral-200 shadow-premium overflow-hidden">
          {loading ? (
            <div className="p-16 flex items-center justify-center text-neutral-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center text-neutral-500">No bookings yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-left text-xs uppercase tracking-widest text-neutral-500">
                  <tr>
                    <th className="px-5 py-3 font-semibold">Customer</th>
                    <th className="px-5 py-3 font-semibold">Vehicle</th>
                    <th className="px-5 py-3 font-semibold">Service</th>
                    <th className="px-5 py-3 font-semibold">Date</th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                    <th className="px-5 py-3 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b) => (
                    <tr key={b.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`booking-row-${b.id}`}>
                      <td className="px-5 py-4">
                        <div className="font-semibold text-brand-black">{b.name}</div>
                        <div className="mt-1 text-xs text-neutral-500 space-y-0.5">
                          <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1 hover:text-brand-red"><Phone className="w-3 h-3" /> {b.phone}</a>
                          <div className="inline-flex items-center gap-1 ml-3"><Mail className="w-3 h-3" /> {b.email}</div>
                        </div>
                        {b.message ? <div className="mt-2 text-xs text-neutral-600 max-w-xs line-clamp-2">{b.message}</div> : null}
                      </td>
                      <td className="px-5 py-4 text-neutral-700">{b.vehicle_make} {b.vehicle_model}</td>
                      <td className="px-5 py-4 text-neutral-700">{b.service_needed}</td>
                      <td className="px-5 py-4 text-neutral-700"><span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {b.preferred_date}</span></td>
                      <td className="px-5 py-4">
                        <select
                          value={b.status}
                          onChange={(e) => onStatus(b.id, e.target.value)}
                          data-testid={`booking-status-${b.id}`}
                          className={`text-xs font-semibold rounded-full border px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-red/40 ${STATUS_STYLES[b.status] || STATUS_STYLES.pending}`}
                        >
                          {STATUSES.map((s) => (<option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>))}
                        </select>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <button onClick={() => onDelete(b.id)} className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-red-600" data-testid={`booking-delete-${b.id}`}>
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
